import React, { useCallback, useState } from "react";
import { Check, Google, InfoOutlined } from "@mui/icons-material";

import Button, { ButtonTypes } from "../../../components/Button";
import {
  useDeleteUserMutation,
  useLogoutMutation,
  useUpdateUserDetailsMutation,
} from "../../../services";
import { isAdult, MIN_AGE } from "../../../utils/age";
import { toIsoDate } from "../../../utils/dates";
import { CompleteProfileProps } from "../types";
import { MonthOption } from "./types";
import {
  Actions,
  Avatar,
  Brand,
  BrandMark,
  BrandName,
  Card,
  DateColumn,
  DateGrid,
  ErrorText,
  Eyebrow,
  FieldLegend,
  Fieldset,
  Footer,
  FooterLink,
  FooterLinks,
  Identity,
  IdentityEmail,
  IdentityInfo,
  IdentityMeta,
  IdentityName,
  Intro,
  Main,
  MonthSelect,
  Page,
  PartInput,
  PartLabel,
  PrivacyLink,
  PrivacyNote,
  ProviderGlyph,
  Subtitle,
  Title,
  TopBar,
  VerifiedBadge,
} from "./styles";

const MONTH_OPTIONS: MonthOption[] = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
].map((label, index) => ({ value: String(index), label }));

const MIN_BIRTH_YEAR = 1900;

const GoogleGlyph = (
  <ProviderGlyph aria-hidden>
    <Google />
  </ProviderGlyph>
);

export const CompleteProfilePage: React.FC<CompleteProfileProps> = ({
  user,
  onComplete,
  onExit,
}) => {
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [year, setYear] = useState("");
  const [error, setError] = useState("");

  const [updateUserDetails, { isLoading: loading }] =
    useUpdateUserDetailsMutation();
  const [deleteUser, { isLoading: exiting }] = useDeleteUserMutation();
  const [logout, { isLoading: signingOut }] = useLogoutMutation();

  const busy = loading || exiting || signingOut;
  const hasError = Boolean(error);
  const currentYear = new Date().getFullYear();

  const fullName = `${user.name} ${user.lastName}`.trim() || user.email;
  const initials =
    `${user.name.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase() ||
    user.email.charAt(0).toUpperCase();
  const isGoogle = user.authProvider === "google";

  const handleMonthChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      setMonth(event.target.value);
      setError("");
    },
    [],
  );

  const handleDayChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setDay(event.target.value.replace(/\D/g, "").slice(0, 2));
      setError("");
    },
    [],
  );

  const handleYearChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setYear(event.target.value.replace(/\D/g, "").slice(0, 4));
      setError("");
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (month === "" || day === "" || year === "") {
      setError("Please enter your complete date of birth");
      return;
    }

    const monthIndex = Number(month);
    const dayNum = Number(day);
    const yearNum = Number(year);

    const dateOfBirth = new Date(yearNum, monthIndex, dayNum);
    const isRealDate =
      yearNum >= MIN_BIRTH_YEAR &&
      yearNum <= currentYear &&
      dateOfBirth.getFullYear() === yearNum &&
      dateOfBirth.getMonth() === monthIndex &&
      dateOfBirth.getDate() === dayNum;

    if (!isRealDate) {
      setError("Please enter a valid date");
      return;
    }

    if (!isAdult(dateOfBirth)) {
      setError(`You must be at least ${MIN_AGE} years old to use this service`);
      return;
    }

    try {
      await updateUserDetails({ dateOfBirth: toIsoDate(dateOfBirth) }).unwrap();
      onComplete();
    } catch {
      // errorToastMiddleware raises the toast; stay on the form.
    }
  }, [month, day, year, currentYear, updateUserDetails, onComplete]);

  const handleDelete = useCallback(async () => {
    try {
      await deleteUser().unwrap();
      onExit();
    } catch {
      // errorToastMiddleware raises the toast; stay on the form.
    }
  }, [deleteUser, onExit]);

  const handleSignOut = useCallback(async () => {
    try {
      await logout().unwrap();
      onExit();
    } catch {
      // errorToastMiddleware raises the toast; stay on the form.
    }
  }, [logout, onExit]);

  return (
    <Page>
      <TopBar>
        <Brand>
          <BrandMark>N</BrandMark>
          <BrandName>Net Worth Tracker</BrandName>
        </Brand>
        <Button
          type={ButtonTypes.clear}
          size="small"
          onClick={handleSignOut}
          disabled={busy}
          data-testid="complete-signout"
        >
          {signingOut ? "Signing out..." : "Not you? Sign out"}
        </Button>
      </TopBar>

      <Main>
        <Card>
          <Intro>
            <Eyebrow>One quick step</Eyebrow>
            <Title>Confirm your date of birth</Title>
            <Subtitle>
              Net Worth Tracker is for adults managing their own finances. We
              just need to confirm you&apos;re {MIN_AGE} or older.
            </Subtitle>
          </Intro>

          <Identity>
            <Avatar>{initials}</Avatar>
            <IdentityInfo>
              <IdentityName>{fullName}</IdentityName>
              <IdentityMeta>
                {isGoogle ? GoogleGlyph : null}
                <IdentityEmail>{user.email}</IdentityEmail>
              </IdentityMeta>
            </IdentityInfo>
            <VerifiedBadge aria-hidden>
              <Check />
            </VerifiedBadge>
          </Identity>

          <Fieldset>
            <FieldLegend htmlFor="complete-dob-month">
              Date of birth
            </FieldLegend>
            <DateGrid>
              <DateColumn>
                <PartLabel>Month</PartLabel>
                <MonthSelect
                  id="complete-dob-month"
                  data-testid="complete-dob-month"
                  value={month}
                  onChange={handleMonthChange}
                  disabled={busy}
                  $hasError={hasError}
                  aria-label="Birth month"
                >
                  <option value="" disabled>
                    Month
                  </option>
                  {MONTH_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </MonthSelect>
              </DateColumn>
              <DateColumn>
                <PartLabel>Day</PartLabel>
                <PartInput
                  data-testid="complete-dob-day"
                  value={day}
                  onChange={handleDayChange}
                  placeholder="DD"
                  inputMode="numeric"
                  maxLength={2}
                  disabled={busy}
                  $hasError={hasError}
                  aria-label="Birth day"
                  autoComplete="bday-day"
                />
              </DateColumn>
              <DateColumn>
                <PartLabel>Year</PartLabel>
                <PartInput
                  data-testid="complete-dob-year"
                  value={year}
                  onChange={handleYearChange}
                  placeholder="YYYY"
                  inputMode="numeric"
                  maxLength={4}
                  disabled={busy}
                  $hasError={hasError}
                  aria-label="Birth year"
                  autoComplete="bday-year"
                />
              </DateColumn>
            </DateGrid>
            {hasError ? <ErrorText role="alert">{error}</ErrorText> : null}
          </Fieldset>

          <PrivacyNote>
            <InfoOutlined />
            <span>
              We use your date of birth only to confirm eligibility and tailor
              tax-year defaults. It&apos;s never shared. See our{" "}
              <PrivacyLink href="/privacy">Privacy Policy</PrivacyLink>.
            </span>
          </PrivacyNote>

          <Actions>
            <Button
              type={ButtonTypes.primary}
              isFullWidth
              size="large"
              isLoading={loading}
              disabled={exiting || signingOut}
              onClick={handleSubmit}
            >
              {loading ? "Confirming..." : "Confirm & continue"}
            </Button>
            <Button
              type={ButtonTypes.revert}
              isFullWidth
              size="large"
              isLoading={exiting}
              disabled={loading || signingOut}
              onClick={handleDelete}
            >
              {exiting ? "Deleting your data..." : "Cancel & delete my data"}
            </Button>
          </Actions>
        </Card>
      </Main>

      <Footer>
        <span>© {currentYear} Net Worth Tracker</span>
        <FooterLinks>
          <FooterLink href="/privacy">Privacy</FooterLink>
          <FooterLink href="/terms">Terms</FooterLink>
        </FooterLinks>
      </Footer>
    </Page>
  );
};
